import React, { Component } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import Highlighter from 'react-highlight-words';

import classNames from 'classnames';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import config from '@/config';
import api from '@/shared/api';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import handleHttpError from '@/shared/services/HandleHttpError';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { ToDoIcon } from '@/shared/components/Icons';
import intlShape from '@/shared/utils/intlShape';
import FormGroupPart from './FormGroupPart';
import messages from '../registrationMessages';
import FormattedDateShort from '../../FormattedDateShort';

const taskSearch = (item, searchValue) => {
  if (!searchValue) return true;
  if (!item) return false;
  if (!item.search) return false;
  return item.search.toUpperCase().indexOf(searchValue.toUpperCase()) >= 0;
};
const renderItem = (item, searchValue) => (
  <span>
    {!item.isToDo && (
      <i
        className={classNames('fa', {
          'margin-10L': item.level === 0,
          'fa-circle greyed-out': item.status === 0,
          'fa-circle orange': item.status === 1,
          'fa-circle green': item.status === 2,
          'fa-minus-circle text-danger': item.status === 3,
        })}
      />
    )}
    {item.isToDo && (
      <ToDoIcon
        className={classNames('margin-10L', {
          'greyed-out': item.status === 0,
          orange: item.status === 1,
          green: item.status === 2,
        })}
      />
    )}
    <span style={{ marginLeft: '3px' }}>
      {searchValue ? (
        <Highlighter
          highlightClassName="bold"
          searchWords={[searchValue]}
          textToHighlight={item.label}
        />
      ) : (
        item.label
      )}
    </span>
    {item.deadline && (
      <span style={{ marginLeft: '3px' }}>
        (<FormattedDateShort value={item.deadline} />)
      </span>
    )}
  </span>
);

class TaskFormGroup extends Component {
  state = {
    initialTask: null,
    tasks: [],
  };

  componentDidUpdate(prevProps) {
    const { plannedTaskId, invoiceStreamId, mainTaskId } = this.props;
    if (prevProps.invoiceStreamId !== invoiceStreamId || prevProps.mainTaskId !== mainTaskId) {
      this.retrieveTasks();

      return;
    }
    if (prevProps.plannedTaskId !== plannedTaskId) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(({ tasks }) => ({
        // Find task in current tasks
        initialTask: plannedTaskId ? tasks.find(x => x.plannedTaskId === plannedTaskId) : null,
      }));
    }
  }

  getTaskLabel = task => {
    const { intl } = this.props;
    return (
      task.plannedTaskName ||
      (task.plannedTaskTemplateId
        ? intl.formatMessage(
            listItemMsg(
              'tasktemplate',
              task.plannedTaskTemplateId,
              task.plannedTaskTemplateName || '',
            ),
          )
        : '')
    );
  };

  componentDidMount() {
    this.retrieveTasks();
  }

  retrieveTasks = () => {
    const { plannedTaskId, invoiceStreamId, mainTaskId, intl } = this.props;
    if (!invoiceStreamId) {
      this.setState({ tasks: [] });
      return;
    }
    return api.base
      .post(`${config.appUrls.tasks}/api/PlannedTasks/ForRegistrations`, {
        invoiceStreamId,
        mainTaskId,
        includeIds: plannedTaskId ? [plannedTaskId] : [],
      })
      .then(
        ({ data }) => {
          if (!data) return;

          const tasks = data
            .reduce((array, orgMainTask, mainTaskIndex) => {
              const { children = null, ...mainTask } = orgMainTask;
              const mainTaskLabel = this.getTaskLabel(mainTask);
              const mappedChildren = (children || []).map((orgSubTask, subTaskIndex) => {
                const subtaskLabel = this.getTaskLabel(orgSubTask);
                return {
                  ...orgSubTask,
                  label: subtaskLabel,
                  id: orgSubTask.plannedTaskId,
                  key: orgSubTask.plannedTaskId,
                  search: `${mainTaskLabel};${subtaskLabel}`,
                  level: 1,
                  order: mainTaskIndex * 100 + (subTaskIndex + 1),
                };
              });

              const childLabels = mappedChildren.map(x => x.label).join(';');

              return array.concat([
                {
                  ...mainTask,
                  label: mainTaskLabel,
                  id: mainTask.plannedTaskId,
                  key: mainTask.plannedTaskId,
                  search: `${mainTaskLabel};${childLabels}`,
                  level: 0,
                  order: mainTaskIndex * 100,
                },
                ...mappedChildren,
              ]);
            }, [])
            .filter(x => !!x.label)
            .sort(dynamicSortMultiple('order', 'label'));
          const initialTask = plannedTaskId
            ? tasks.find(x => x.plannedTaskId === plannedTaskId)
            : null;
          this.setState({
            tasks,
            initialTask,
          });
        },
        error => {
          handleHttpError(intl)(error);
        },
      );
  };

  onTaskChanged = task => {
    if (task === undefined) return;
    const { onChange } = this.props;
    onChange(task);
  };

  render() {
    const { intl, disabled, showValidation, required } = this.props;
    const { initialTask, tasks } = this.state;
    if (!tasks || !tasks.length) return null;
    const invalid = showValidation && required && !initialTask;

    return (
      <div className="form-group">
        <FormGroupPart label={messages.Task} inputCols="col-sm-10" invalid={invalid}>
          <AutoComplete
            items={tasks}
            placeholder={intl.formatMessage(messages.Task)}
            onChange={item => this.onTaskChanged(item)}
            disabled={disabled}
            mode="edit"
            initialSelectedItem={initialTask}
            showAll
            renderItem={renderItem}
            filterFunc={taskSearch}
            isSmall
          />
        </FormGroupPart>
      </div>
    );
  }

  static propTypes = {
    intl: intlShape.isRequired,
    onChange: PropTypes.func.isRequired,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    showValidation: PropTypes.bool,
    invoiceStreamId: PropTypes.string,
    plannedTaskId: PropTypes.string,
    // If the mainTaskId is provided, only this mainTask and its subtasks can be selected
    mainTaskId: PropTypes.string,
  };

  static defaultProps = {
    disabled: false,
    required: false,
    showValidation: false,
    invoiceStreamId: null,
    plannedTaskId: null,
    mainTaskId: null,
  };
}

export default injectIntl(TaskFormGroup);
